import React, { useState } from 'react';
import { useConversations } from '../hooks/useConversations';
import { useChatData } from '../context/ChatContext';
import ConversationItem from './ConversationItem';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faPlus, faUsers } from '@fortawesome/free-solid-svg-icons';

interface ConversationListProps {
  onNewChat: () => void;
  onNewGroup: () => void;
}

const ConversationList: React.FC<ConversationListProps> = ({ onNewChat, onNewGroup }) => {
  const { t } = useTranslation();
  const {
    conversations,
    isSyncing,
    unreadCounts,
    searchQuery,
    conversationFilter,
    setSearchQuery,
    setFilter,
    syncConversations,
  } = useConversations();
  const { activeConversationId, selectConversation } = useChatData();

  return (
    <div className="chat-conversation-list">
      {/* Header */}
      <div className="chat-conversation-list__header">
        <h5 className="chat-conversation-list__title">
          {t('chat.messages', 'Messages')}
        </h5>
        <div className="chat-conversation-list__actions">
          <button
            className="chat-btn chat-btn--icon"
            onClick={syncConversations}
            disabled={isSyncing}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faArrowsRotate} spin={isSyncing} />
          </button>
          <button
            className="chat-btn chat-btn--icon"
            onClick={onNewChat}
            title={t('chat.newConversation', 'New Chat')}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
          <button
            className="chat-btn chat-btn--icon"
            onClick={onNewGroup}
            title={t('chat.newGroup', 'New Group')}
          >
            <FontAwesomeIcon icon={faUsers} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="chat-conversation-list__search">
        <input
          type="text"
          className="chat-search-input"
          placeholder={t('chat.search', 'Search conversations...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Filter tabs */}
      <div className="chat-conversation-list__filters">
        {(['all', 'dms', 'groups'] as const).map((f) => (
          <button
            key={f}
            className={`chat-filter-btn ${conversationFilter === f ? 'chat-filter-btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? t('chat.all', 'All') : f === 'dms' ? t('chat.dms', 'DMs') : t('chat.groups', 'Groups')}
          </button>
        ))}
      </div>

      {/* Conversation items */}
      <div className="chat-conversation-list__items">
        {conversations.length === 0 ? (
          <div className="chat-conversation-list__empty">
            <p>{t('chat.noConversations', 'No conversations yet')}</p>
            <button className="chat-btn chat-btn--primary" onClick={onNewChat}>
              {t('chat.startChat', 'Start a Chat')}
            </button>
          </div>
        ) : (
          conversations.map((convo) => (
            <ConversationItem
              key={convo.id}
              conversation={convo}
              isActive={activeConversationId === convo.id}
              unreadCount={unreadCounts[convo.id] || 0}
              onClick={() => selectConversation(convo.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationList;
